import {
	faArrowLeft,
	faSpinner,
	faTag,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ResilientImage, useTranslation } from '@supply-mover-2-0/web-ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { JSX } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { StorefrontItemCard } from '../components/dashboard/widgets/StorefrontItemCard';
import { usePortalAuth } from '../hooks/usePortalAuth';
import * as dashboardService from '../services/portalDashboardService';

/**
 * Default click-through destination for any promo tile when the admin
 * hasn't picked an explicit `ctaLinkType`. The page renders the promo
 * hero (banner image, headline, subheadline, terms) and a grid of the
 * items participating in the BUY set so the customer can immediately
 * navigate into a participating item's product detail page.
 *
 * Anonymous-readable. The backend marks the endpoint
 * `@Security.PublicPortalOptional()`, so a visitor without a session
 * still gets the public promo metadata; per-customer pricing layers
 * on later via the items grid's product detail navigations. The
 * earlier `enabled: token != null` gate dropped the entire query for
 * anonymous visitors and stalled the page on "Promo not available."
 */
export function PromoDetailPage(): JSX.Element {
	const { t } = useTranslation();
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { token } = usePortalAuth();
	const queryClient = useQueryClient();

	const promoId = id != null ? Number(id) : null;
	const queryKey = ['portal-promo-detail', promoId];

	const { data, isLoading, isError } = useQuery({
		queryKey,
		queryFn: () =>
			dashboardService.getStorefrontPromoDetail(promoId as number, token),
		enabled: promoId != null && Number.isFinite(promoId),
	});

	if (isLoading) {
		return (
			<div className="px-4 py-12 text-center">
				<FontAwesomeIcon
					icon={faSpinner}
					className="animate-spin text-2xl text-gray-400 mb-3"
				/>
				<p className="text-sm text-brand-muted">{t('Loading promo...')}</p>
			</div>
		);
	}

	if (isError || data?.data == null) {
		return (
			<div className="px-4 py-12 text-center">
				<p className="text-sm text-brand-muted">{t('Promo not available.')}</p>
				<button
					type="button"
					onClick={() => navigate('/')}
					className="mt-4 px-4 py-2 text-sm text-portal-accent hover:opacity-80"
				>
					{t('Back to home')}
				</button>
			</div>
		);
	}

	const { promo, items, categoryNames } = data.data;
	const banner = promo.imagesJson?.bannerUrl ?? null;

	// Quick badge text: same shape the LINE-scope engine emits in
	// catalog cards. Renders a single short tag so the customer
	// immediately sees the discount magnitude (e.g. "20% OFF",
	// "$15 OFF"). Order-scope and free-gift promos fall through to
	// the generic "ON SALE" since their numeric value isn't useful
	// without cart context.
	let badge = 'ON SALE';
	if (promo.discountType === 'PERCENT') {
		badge = `${Math.round(promo.discountValue)}% OFF`;
	} else if (promo.discountType === 'DOLLARS') {
		badge = `$${promo.discountValue.toFixed(2)} OFF`;
	} else if (promo.discountType === 'BUY_X_GET_Y') {
		badge = `BUY ${promo.buyQty} GET ${promo.getQty}`;
	}

	return (
		<div className="px-4 py-4 max-w-5xl mx-auto">
			{/* Back button. Storefront pages typically navigate back
			    to the homepage since this is reached from a homepage
			    promo tile. */}
			<button
				type="button"
				onClick={() => navigate(-1)}
				className="flex items-center gap-2 text-sm text-brand-muted hover:text-gray-900 dark:hover:text-gray-100 mb-4 min-h-touch"
			>
				<FontAwesomeIcon icon={faArrowLeft} />
				<span>{t('Back')}</span>
			</button>

			{/* Hero panel. The banner artwork already has the headline,
			    discount value, subhead, and CTA baked in (admins
			    upload Weee!-style designed banners), so we just show
			    the image. No React text overlay — that double-rendered
			    text against the baked text. The 16:9 hero banner is
			    used here regardless of which size variant a widget on
			    the homepage used. Falls back to a branded gradient
			    with a screen-reader-friendly headline when no banner
			    has been uploaded. */}
			{banner != null ? (
				<div className="relative rounded-2xl overflow-hidden border border-brand-border">
					<ResilientImage
						src={banner}
						alt={promo.headline ?? promo.name}
						className="block w-full h-auto"
						onLikelyStaleUrl={() => {
							void queryClient.invalidateQueries({ queryKey });
						}}
						fallback={null}
					/>
					{/* Visually-hidden inline badge for context — surfaces the
					    discount magnitude in the page title bar / a11y tree. */}
					<span className="sr-only">
						{t(badge)} — {t(promo.headline ?? promo.name)}
					</span>
				</div>
			) : (
				<div
					className="relative rounded-2xl overflow-hidden border border-brand-border min-h-[220px] md:min-h-[280px] p-6 md:p-10 flex flex-col justify-end text-white"
					style={{ background: 'linear-gradient(135deg, #fbbf24, #f59e0b)' }}
				>
					<div className="relative z-10">
						<span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/95 text-brand-text text-xs font-bold uppercase tracking-wide">
							<FontAwesomeIcon icon={faTag} />
							{t(badge)}
						</span>
						<h1 className="mt-3 text-2xl md:text-4xl font-bold drop-shadow">
							{t(promo.headline ?? promo.name)}
						</h1>
						{promo.subheadline != null && promo.subheadline.length > 0 && (
							<p className="mt-2 text-sm md:text-base opacity-90 drop-shadow max-w-2xl">
								{t(promo.subheadline)}
							</p>
						)}
					</div>
				</div>
			)}

			{/* Description (longer prose if admin filled it in). */}
			{promo.description != null && promo.description.length > 0 && (
				<div className="mt-6 text-sm md:text-base text-gray-700 dark:text-gray-300 max-w-3xl">
					{t(promo.description)}
				</div>
			)}

			{/* Targeted-category chips. Most ecommerce promos in this
			    deployment scope discounts to whole categories rather
			    than to a hand-picked SKU list (e.g. "15% off pantry
			    essentials" → Sauce, Spice, Noodles, Grain, Oil), so
			    the customer needs an obvious "where do I shop to use
			    this?" affordance. Each chip deep-links into the
			    catalog filtered to that category. Hidden when the
			    promo has only explicit-item targeting (item-level
			    promos render the items grid directly below and
			    don't need a category-shortcut row). */}
			{categoryNames.length > 0 && (
				<div className="mt-6">
					<h2 className="text-xs uppercase tracking-wide text-brand-muted mb-2">
						{t('Applies to')}
					</h2>
					<div className="flex flex-wrap gap-2">
						{categoryNames.map((cat) => (
							<button
								key={cat}
								type="button"
								onClick={() =>
									navigate(`/catalog?category=${encodeURIComponent(cat)}`)
								}
								className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-brand-input border border-brand-border text-sm font-medium text-gray-800 dark:text-gray-100 hover:bg-brand-card transition-colors"
							>
								<FontAwesomeIcon icon={faTag} className="text-[10px]" />
								{t(cat)}
							</button>
						))}
					</div>
				</div>
			)}

			{/* Items grid. Cards link to the product detail page where
			    pricing + inventory + add-to-cart live. Keeps this page
			    light — no need to re-implement the catalog's pricing
			    enrichment just to render thumbnails. */}
			<div className="mt-8">
				<h2 className="text-base md:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
					{t('Items in this promo')} ({items.length})
				</h2>
				{items.length === 0 ? (
					<p className="text-sm text-brand-muted">
						{categoryNames.length > 0
							? t(
									'This promo applies to whole categories — tap a chip above to shop those items.',
								)
							: t(
									'This promo applies to your whole order — browse the catalog to use it.',
								)}
					</p>
				) : (
					<div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
						{items.map((item) => (
							<StorefrontItemCard
								key={item.id}
								item={item}
								onNavigate={() => navigate(`/product/${item.id}`)}
								onLikelyStaleUrl={() => {
									void queryClient.invalidateQueries({ queryKey });
								}}
							/>
						))}
					</div>
				)}
			</div>

			{/* Terms — small print, collapsible-style block. */}
			{promo.termsAndConditions != null &&
				promo.termsAndConditions.length > 0 && (
					<div className="mt-8 p-4 bg-brand-input rounded-lg">
						<h3 className="text-xs font-semibold uppercase text-brand-muted mb-2">
							{t('Terms & conditions')}
						</h3>
						<p className="text-xs md:text-sm text-gray-700 dark:text-gray-300 whitespace-pre-line">
							{t(promo.termsAndConditions)}
						</p>
					</div>
				)}
		</div>
	);
}
